import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  ArrowLeft,
  CalendarDays,
  Loader2,
  Pencil,
  Plus,
  RefreshCw,
  Truck,
  X,
} from "lucide-react";

import i18next from "i18next";

import DriverAvailableVehicleModal from "./DriverAvailableVehicleModal.jsx";

const COPY = {
  ru: {
    title: "Мои машины",
    add: "Добавить машину",
    active: "Активные",
    history: "История",
    loading: "Загрузка машин…",
    loadError: "Не удалось загрузить машины.",
    emptyActive: "Активных машин пока нет.",
    emptyHistory: "История пока пуста.",
    edit: "Редактировать",
    close: "Завершить",
    closing: "Завершаем…", renew: "Подтвердить: свободен", renewing: "Обновляем…", renewError: "Не удалось обновить объявление.",
    closeConfirm: "Завершить публикацию этой машины?",
    closeError: "Не удалось завершить публикацию.",
    available: "Опубликована",
    expires: "Статус",
    closed: "Завершено",
    expired: "Срок истёк",
    negotiable: "Цена договорная",
    partial: "Догруз",
    capacity: "Грузоподъёмность",
    volume: "Объём",
    refresh: "Обновить",
    back: "Назад",
    tons: "т",
    cubic: "м³",
  },

  uz: {
    title: "Менинг машиналарим",
    add: "Машина қўшиш",
    active: "Фаол",
    history: "Тарих",
    loading: "Машиналар юкланмоқда…",
    loadError: "Машиналарни юклаб бўлмади.",
    emptyActive: "Ҳозирча фаол машина йўқ.",
    emptyHistory: "Тарих ҳозирча бўш.",
    edit: "Таҳрирлаш",
    close: "Якунлаш",
    closing: "Якунланмоқда…", renew: "Бўшлигини тасдиқлаш", renewing: "Янгиланмоқда…", renewError: "Эълонни янгилаб бўлмади.",
    closeConfirm: "Бу машина эълонини якунлайсизми?",
    closeError: "Эълонни якунлаб бўлмади.",
    available: "Эълон қилинган",
    expires: "Ҳолат",
    closed: "Якунланган",
    expired: "Муддати ўтган",
    negotiable: "Нарх келишилади",
    partial: "Догруз",
    capacity: "Юк кўтариши",
    volume: "Ҳажми",
    refresh: "Янгилаш",
    back: "Орқага",
    tons: "т",
    cubic: "м³",
  },

  tr: {
    title: "Araçlarım",
    add: "Araç ekle",
    active: "Aktif",
    history: "Geçmiş",
    loading: "Araçlar yükleniyor…",
    loadError: "Araçlar yüklenemedi.",
    emptyActive: "Aktif araç yok.",
    emptyHistory: "Geçmiş henüz boş.",
    edit: "Düzenle",
    close: "Bitir",
    closing: "Bitiriliyor…", renew: "Müsaitliği onayla", renewing: "Yenileniyor…", renewError: "İlan yenilenemedi.",
    closeConfirm: "Bu araç ilanını bitirmek istiyor musunuz?",
    closeError: "İlan bitirilemedi.",
    available: "Yayınlandı",
    expires: "Durum",
    closed: "Bitti",
    expired: "Süresi doldu",
    negotiable: "Fiyat görüşülür",
    partial: "Parsiyel",
    capacity: "Kapasite",
    volume: "Hacim",
    refresh: "Yenile",
    back: "Geri",
    tons: "t",
    cubic: "m³",
  },
};

const BODY_LABELS = {
  ru: {
    TENT: "Тент",
    REFRIGERATOR: "Рефрижератор",
    ISOTHERM: "Изотерм",
    VAN: "Фургон",
    CONTAINER: "Контейнер",
    FLATBED: "Платформа",
    MEGA: "Mega",
    LABO: "Лабо",
    SPECIAL: "Спецтехника",
    OTHER: "Другое",
  },
  uz: {
    TENT: "Тент",
    REFRIGERATOR: "Рефрижератор",
    ISOTHERM: "Изотерм",
    VAN: "Фургон",
    CONTAINER: "Контейнер",
    FLATBED: "Платформа",
    MEGA: "Mega",
    LABO: "Лабо",
    SPECIAL: "Махсус техника",
    OTHER: "Бошқа",
  },
  tr: {
    TENT: "Tenteli",
    REFRIGERATOR: "Frigorifik",
    ISOTHERM: "İzoterm",
    VAN: "Kapalı kasa",
    CONTAINER: "Konteyner",
    FLATBED: "Platform",
    MEGA: "Mega",
    LABO: "Labo",
    SPECIAL: "Özel ekipman",
    OTHER: "Diğer",
  },
};

function text(value) {
  return String(value == null ? "" : value).trim();
}

function languageCode() {
  const raw = text(i18next.language).toLowerCase();

  if (raw.startsWith("uz")) return "uz";
  if (raw.startsWith("tr")) return "tr";

  return "ru";
}

function bearer(token) {
  const value = text(token);

  if (!value) return "";

  return /^bearer\s+/i.test(value)
    ? value
    : `Bearer ${value}`;
}

function formatDate(value, lang) {
  if (!value) return "—";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return "—";
  }

  const locale =
    lang === "uz"
      ? "uz-UZ"
      : lang === "tr"
        ? "tr-TR"
        : "ru-RU";

  return new Intl.DateTimeFormat(locale, {
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
}

function formatAmount(value, currency, lang) {
  const amount = Number(value);

  if (!Number.isFinite(amount) || amount <= 0) {
    return "";
  }

  const locale =
    lang === "uz"
      ? "uz-UZ"
      : lang === "tr"
        ? "tr-TR"
        : "ru-RU";

  return `${new Intl.NumberFormat(locale, {
    maximumFractionDigits: 0,
  }).format(amount)} ${text(currency)}`;
}

function itemTime(item) {
  const raw =
    item?.closedAt ||
    item?.updatedAt ||
    item?.createdAt ||
    item?.expiresAt;

  const ms = raw ? new Date(raw).getTime() : 0;

  return Number.isFinite(ms) ? ms : 0;
}

function vehicleName(item) {
  return [
    text(item?.vehicle?.carBrand),
    text(item?.vehicle?.carModel),
    text(item?.vehicle?.carYear),
  ]
    .filter(Boolean)
    .join(" ");
}

export default function DriverMyVehicles({
  open,
  onClose,
  driverToken,
  driver,
  onChanged,
}) {
  const lang = languageCode();
  const t = COPY[lang] || COPY.ru;
  const bodyLabels = BODY_LABELS[lang] || BODY_LABELS.ru;

  const [tab, setTab] = useState("ACTIVE");
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [closingId, setClosingId] = useState("");
  const [renewingId, setRenewingId] = useState("");

  const [vehicleModalOpen, setVehicleModalOpen] =
    useState(false);

  const [editingVehicle, setEditingVehicle] =
    useState(null);

  const auth = useMemo(
    () => bearer(driverToken),
    [driverToken]
  );

  const load = useCallback(async () => {
    if (!auth) {
      setItems([]);
      setError(t.loadError);
      return;
    }

    setBusy(true);
    setError("");

    try {
      const response = await fetch(
        "/api/driver/available-vehicles",
        {
          method: "GET",
          credentials: "include",
          cache: "no-store",
          headers: {
            Authorization: auth,
          },
        }
      );

      const data = await response
        .json()
        .catch(() => ({}));

      if (!response.ok) {
        throw new Error(
          data?.error ||
          data?.message ||
          `HTTP ${response.status}`
        );
      }

      setItems(
        Array.isArray(data?.items)
          ? data.items
          : []
      );
    } catch (loadError) {
      console.error(
        "[driver-my-vehicles/load]",
        loadError
      );

      setItems([]);
      setError(t.loadError);
    } finally {
      setBusy(false);
    }
  }, [auth, t.loadError]);

  useEffect(() => {
    if (!open) return;

    setTab("ACTIVE");
    setEditingVehicle(null);
    setVehicleModalOpen(false);

    load().catch(() => {});
  }, [open, load]);

  useEffect(() => {
    if (!open) return undefined;

    const onKeyDown = (event) => {
      if (
        event.key === "Escape" &&
        !vehicleModalOpen
      ) {
        onClose?.();
      }
    };

    window.addEventListener(
      "keydown",
      onKeyDown
    );

    return () => {
      window.removeEventListener(
        "keydown",
        onKeyDown
      );
    };
  }, [
    open,
    vehicleModalOpen,
    onClose,
  ]);

  const activeItems = useMemo(
    () =>
      items
        .filter(
          (item) =>
            text(item?.status).toUpperCase() ===
            "ACTIVE"
        )
        .sort(
          (a, b) =>
            itemTime(b) - itemTime(a)
        ),
    [items]
  );

  const historyItems = useMemo(
    () =>
      items
        .filter(
          (item) =>
            text(item?.status).toUpperCase() !==
            "ACTIVE"
        )
        .sort(
          (a, b) =>
            itemTime(b) - itemTime(a)
        ),
    [items]
  );

  const visibleItems =
    tab === "ACTIVE"
      ? activeItems
      : historyItems;

  const openCreate = () => {
    setEditingVehicle(null);
    setVehicleModalOpen(true);
  };

  const openEdit = (item) => {
    setEditingVehicle(item);
    setVehicleModalOpen(true);
  };

  const afterSaved = async () => {
    setVehicleModalOpen(false);
    setEditingVehicle(null);

    await load();

    onChanged?.();
  };

  const renewVehicle = async (item) => {
    const id = text(item?.id);
    if (!id || renewingId) return;
    setRenewingId(id);
    setError("");
    try {
      const response = await fetch(
        `/api/driver/available-vehicles/${encodeURIComponent(id)}/renew`,
        {
          method: "POST",
          credentials: "include",
          headers: { Authorization: auth },
        }
      );
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data?.error || data?.message || `HTTP ${response.status}`);
      }
      await load();
      onChanged?.();
    } catch (renewError) {
      console.error("[driver-my-vehicles/renew]", renewError);
      setError(t.renewError);
    } finally {
      setRenewingId("");
    }
  };

  const closeVehicle = async (item) => {
    const id = text(item?.id);

    if (!id || closingId) return;

    const accepted = window.confirm(
      t.closeConfirm
    );

    if (!accepted) return;

    setClosingId(id);
    setError("");

    try {
      const response = await fetch(
        `/api/driver/available-vehicles/${encodeURIComponent(id)}/close`,
        {
          method: "POST",
          credentials: "include",
          headers: {
            Authorization: auth,
          },
        }
      );

      const data = await response
        .json()
        .catch(() => ({}));

      if (!response.ok) {
        throw new Error(
          data?.error ||
          data?.message ||
          `HTTP ${response.status}`
        );
      }

      await load();

      onChanged?.();
    } catch (closeError) {
      console.error(
        "[driver-my-vehicles/close]",
        closeError
      );

      setError(t.closeError);
    } finally {
      setClosingId("");
    }
  };

  if (!open) return null;

  return (
    <>
      <div
        className="fixed inset-0 z-[10060] overflow-y-auto bg-slate-950 text-white"
        role="dialog"
        aria-modal="true"
        aria-label={t.title}

        data-reisor-theme-surface="driver-my-vehicles">
        <div className="mx-auto min-h-full w-full max-w-[760px] bg-slate-900">
          <div className="sticky top-0 z-10 border-b border-slate-700 bg-slate-900/95 px-4 pb-3 pt-[max(14px,env(safe-area-inset-top))] backdrop-blur">
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={onClose}
                className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl border border-slate-700 bg-slate-800/70 text-white"
                aria-label={t.back}
              >
                <ArrowLeft size={22} />
              </button>

              <div className="min-w-0 flex-1">
                <h2 className="truncate text-xl font-black">
                  {t.title}
                </h2>
              </div>

              <button
                type="button"
                onClick={() => {
                  load().catch(() => {});
                }}
                disabled={busy}
                className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl border border-slate-700 bg-slate-800/70 text-slate-200 disabled:opacity-50"
                aria-label={t.refresh}
                title={t.refresh}
              >
                <RefreshCw
                  size={20}
                  className={
                    busy
                      ? "animate-spin"
                      : ""
                  }
                />
              </button>
            </div>

            <button
              type="button"
              onClick={openCreate}
              className="mt-3 flex w-full items-center justify-center gap-2 rounded-2xl bg-blue-600 px-4 py-3.5 text-base font-black text-white shadow-lg shadow-blue-950/30"
            >
              <Plus size={22} />
              <Truck size={21} />
              <span>{t.add}</span>
            </button>


            {/* REISOR_PHASE2A_PRIMARY_VEHICLE_CARD_V2 */}
            {driver?.bodyType ? (
              <div className="mt-3 rounded-2xl border border-emerald-500/30 bg-emerald-950/20 p-4">
                <div className="flex items-start gap-3">
                  <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-emerald-500/15 text-emerald-300">
                    <Truck size={22} />
                  </div>

                  <div className="min-w-0 flex-1">
                    <div className="text-xs font-black uppercase tracking-wide text-emerald-300">
                      {lang === "uz"
                        ? "Асосий машина"
                        : lang === "tr"
                          ? "Ana araç"
                          : "Основная машина"}
                    </div>

                    <div className="mt-1 text-base font-black text-white">
                      {[
                        text(driver?.bodyType),
                        text(driver?.bodyTypeOther),
                      ]
                        .filter(Boolean)
                        .join(" · ")}
                    </div>

                    <div className="mt-1 text-sm font-semibold text-slate-300">
                      {Number(driver?.capacityTons) > 0
                        ? `${driver.capacityTons} ${t.tons}`
                        : "—"}

                      {Number(driver?.volumeM3) > 0
                        ? ` · ${driver.volumeM3} ${t.cubic}`
                        : ""}
                    </div>

                    <div className="mt-1 text-xs font-semibold text-slate-400">
                      {lang === "uz"
                        ? "Профилда сақланган асосий машина. Бозорга ҳали чиқарилмаган."
                        : lang === "tr"
                          ? "Profilde kayıtlı ana araç. Henüz boş araç olarak yayınlanmadı."
                          : "Основная машина сохранена в профиле. Это ещё не объявление о свободной машине."}
                    </div>
                  </div>
                </div>

                <button
                  type="button"
                  onClick={openCreate}
                  className="mt-3 w-full rounded-xl bg-emerald-600 px-4 py-3 text-sm font-black text-white"
                >
                  {lang === "uz"
                    ? "Бўш машина сифатида эълон қилиш"
                    : lang === "tr"
                      ? "Boş araç olarak yayınla"
                      : "Опубликовать как свободную"}
                </button>
              </div>
            ) : null}

<div className="mt-3 grid grid-cols-2 rounded-2xl border border-slate-700 bg-slate-950/60 p-1">
              <button
                type="button"
                onClick={() =>
                  setTab("ACTIVE")
                }
                className={`rounded-xl px-3 py-3 text-sm font-black ${
                  tab === "ACTIVE"
                    ? "bg-blue-600 text-white"
                    : "text-slate-400"
                }`}
              >
                {t.active} · {activeItems.length}
              </button>

              <button
                type="button"
                onClick={() =>
                  setTab("HISTORY")
                }
                className={`rounded-xl px-3 py-3 text-sm font-black ${
                  tab === "HISTORY"
                    ? "bg-blue-600 text-white"
                    : "text-slate-400"
                }`}
              >
                {t.history} · {historyItems.length}
              </button>
            </div>
          </div>

          <div
            className="space-y-3 p-4"
            style={{
              paddingBottom:
                "calc(env(safe-area-inset-bottom, 0px) + 32px)",
            }}
          >
            {error ? (
              <div className="rounded-2xl border border-red-500/40 bg-red-950/30 p-3 text-sm font-semibold text-red-200">
                {error}
              </div>
            ) : null}

            {busy && items.length === 0 ? (
              <div className="flex items-center justify-center gap-2 py-16 text-sm font-semibold text-slate-400">
                <Loader2
                  size={20}
                  className="animate-spin"
                />
                {t.loading}
              </div>
            ) : null}

            {!busy &&
            visibleItems.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-20 text-center text-slate-500">
                <Truck
                  size={58}
                  strokeWidth={1.5}
                />
                <div className="mt-4 text-lg font-bold">
                  {tab === "ACTIVE"
                    ? t.emptyActive
                    : t.emptyHistory}
                </div>
              </div>
            ) : null}

            {visibleItems.map((item) => {
              const id = text(item?.id);
              const status =
                text(item?.status).toUpperCase();

              const isActive =
                status === "ACTIVE";

              const body =
                bodyLabels[
                  text(
                    item?.bodyType
                  ).toUpperCase()
                ] ||
                text(item?.bodyType) ||
                "—";

              const price =
                text(
                  item?.priceMode
                ).toUpperCase() === "FIXED"
                  ? formatAmount(
                      item?.priceAmount,
                      item?.currency,
                      lang
                    )
                  : t.negotiable;

              const name =
                vehicleName(item);

              return (
                <div
                  key={id}
                  className="rounded-3xl border border-slate-700 bg-slate-950/65 p-4 shadow-xl shadow-black/10"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="text-lg font-black text-blue-300">
                        {text(item?.from) ||
                          "—"}{" "}
                        →{" "}
                        {text(item?.to) ||
                          "—"}
                      </div>

                      <div className="mt-2 flex flex-wrap gap-2">
                        <span className="rounded-xl bg-slate-800 px-2.5 py-1 text-[13px] font-bold text-slate-200">
                          {body}
                        </span>

                        {item?.partialLoad ? (
                          <span className="rounded-xl bg-emerald-950/60 px-2.5 py-1 text-[13px] font-bold text-emerald-300">
                            {t.partial}
                          </span>
                        ) : null}

                        {!isActive ? (
                          <span className="rounded-xl bg-slate-800 px-2.5 py-1 text-[13px] font-bold text-slate-300">
                            {status ===
                            "CLOSED"
                              ? t.closed
                              : t.expired}
                          </span>
                        ) : null}
                      </div>
                    </div>

                    <Truck
                      size={24}
                      className="shrink-0 text-blue-400"
                    />
                  </div>

                  {name ? (
                    <div className="mt-4 text-sm text-slate-300">
                      <span className="font-bold text-slate-400">
                        {t.title.replace(
                          /^Мои |^Менинг |ım$/g,
                          ""
                        )}
                      </span>
                      <span className="ml-2 font-bold text-white">
                        {name}
                      </span>
                    </div>
                  ) : null}

                  <div className="mt-4 grid grid-cols-2 gap-2">
                    <div className="rounded-2xl bg-slate-900 p-3">
                      <div className="text-[14px] font-bold text-slate-500">
                        {t.capacity}
                      </div>
                      <div className="mt-1 text-base font-black">
                        {Number(item?.capacityTons) > 0
                          ? `${Number(item.capacityTons)} ${t.tons}`
                          : "—"}
                      </div>
                    </div>

                    <div className="rounded-2xl bg-slate-900 p-3">
                      <div className="text-[14px] font-bold text-slate-500">
                        {t.volume}
                      </div>
                      <div className="mt-1 text-base font-black">
                        {item?.volumeM3 ==
                          null
                          ? "—"
                          : `${Number(
                              item.volumeM3
                            )} ${t.cubic}`}
                      </div>
                    </div>
                  </div>

                  <div className="mt-4 space-y-2 text-sm text-slate-400">
                    <div className="flex items-center gap-2">
                      <CalendarDays
                        size={17}
                      />
                      <span>
                        {t.available}:{" "}
                        <strong className="text-slate-200">
                          {formatDate(
                            item?.availableFrom,
                            lang
                          )}
                        </strong>
                      </span>
                    </div>

                    <div className="flex items-center gap-2">
                      <CalendarDays
                        size={17}
                      />
                      <span>
                        {t.expires}:{" "}
                        <strong className="text-slate-200">
                          {item?.expiresAt ? formatDate(item.expiresAt, lang) : (lang === "uz" ? "Бўш — қўлда банд қилингунча" : lang === "tr" ? "Müsait — meşgul yapılana kadar" : "Свободна — до статуса «Занят»")}
                        </strong>
                      </span>
                    </div>
                  </div>

                  <div className="mt-4 border-t border-slate-800 pt-4">
                    <div className="text-lg font-black text-emerald-400">
                      {price || t.negotiable}
                    </div>

                    {isActive ? (
                      <div className="mt-3 space-y-2">
                        <button
                          type="button"
                          onClick={() => renewVehicle(item)}
                          disabled={renewingId === id}
                          className="flex min-h-[44px] w-full items-center justify-center gap-2 rounded-2xl bg-emerald-600 px-3 py-3 text-sm font-black text-white disabled:opacity-50"
                        >
                          <RefreshCw size={18} className={renewingId === id ? "animate-spin" : ""} />
                          {renewingId === id ? t.renewing : t.renew}
                        </button>
                        <div className="grid grid-cols-2 gap-2">
                        <button
                          type="button"
                          onClick={() =>
                            openEdit(item)
                          }
                          className="flex items-center justify-center gap-2 rounded-2xl bg-blue-600 px-3 py-3 text-sm font-black text-white"
                        >
                          <Pencil
                            size={18}
                          />
                          {t.edit}
                        </button>

                        <button
                          type="button"
                          disabled={
                            closingId ===
                            id
                          }
                          onClick={() =>
                            closeVehicle(
                              item
                            )
                          }
                          className="flex items-center justify-center gap-2 rounded-2xl border border-red-500/50 bg-red-950/30 px-3 py-3 text-sm font-black text-red-300 disabled:opacity-50"
                        >
                          {closingId ===
                          id ? (
                            <Loader2
                              size={18}
                              className="animate-spin"
                            />
                          ) : (
                            <X size={18} />
                          )}

                          {closingId ===
                          id
                            ? t.closing
                            : t.close}
                        </button>
                        </div>
                      </div>
                    ) : null}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <DriverAvailableVehicleModal
        open={vehicleModalOpen}
        onClose={() => {
          setVehicleModalOpen(false);
          setEditingVehicle(null);
        }}
        driverToken={driverToken}
        driver={driver}
        vehicle={editingVehicle}
        onSaved={() => {
          afterSaved().catch(() => {});
        }}
      />
    </>
  );
}
